import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import HStack from "@components/layout/HStack";
import { DefaultResourceTypeEntity } from "@features/settings/services/entities/organizerSettingEntity";
import { useSettingStore, useThemeStore } from "@shared/stores";
import { useSearchParams } from "react-router-dom";
import { MoreHorizontal } from "../icons/IconCustom";

type Props = {
  selectedResourceType?: string;
};

const ResourceTypeToolbar = ({ selectedResourceType }: Props) => {
  const { defaultResourceType, defaultLocations } = useSettingStore();
  const resourceTypes = useMemo(() => defaultLocations.defaultResourceTypes, [defaultLocations]);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentParams = useMemo(() => {
    const params: Record<string, string | string[]> = {};
    for (const [key, value] of searchParams.entries()) {
      if (params[key]) {
        if (Array.isArray(params[key])) {
          (params[key] as string[]).push(value);
        } else {
          params[key] = [params[key] as string, value];
        }
      } else {
        params[key] = value;
      }
    }
    return params;
  }, [searchParams]);

  const resourceType = useMemo(() => {
    return searchParams.get("resourceType") || defaultResourceType;
  }, [searchParams]);

  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleIds, setVisibleIds] = useState<string[]>(resourceTypes.map((i) => i.type));
  const [itemWidths, setItemWidths] = useState<Record<string, number>>({});

  const handleSelectResourceType = (type: string) => {
    setSearchParams({ ...currentParams, resourceType: type }, { replace: true });
  };

  useLayoutEffect(() => {
    const widths: Record<string, number> = {};
    const measurer = document.createElement("span");
    measurer.style.cssText =
      "position:absolute;visibility:hidden;font-size:14px;padding:0 8px;font-weight:400;white-space:nowrap;";
    document.body.appendChild(measurer);

    resourceTypes.forEach((i) => {
      measurer.textContent = i.type;
      widths[i.type] = measurer.offsetWidth;
    });
    document.body.removeChild(measurer);
    setItemWidths(widths);
  }, []);

  const recalc = () => {
    if (!containerRef.current) return;
    if (Object.keys(itemWidths).length === 0) return;

    const containerW = containerRef.current.offsetWidth;
    const gap = 4;
    const menuW = 40;

    let used = 0;
    const visFirst: string[] = [];
    for (const i of resourceTypes) {
      const w = itemWidths[i.type] + gap;
      if (used + w <= containerW) {
        visFirst.push(i.type);
        used += w;
      } else break;
    }

    if (visFirst.length === resourceTypes.length) {
      setVisibleIds(visFirst);
      return;
    }

    used = 0;
    const vis: string[] = [];
    for (const i of resourceTypes) {
      const w = itemWidths[i.type] + gap;
      if (used + w + menuW <= containerW) {
        vis.push(i.type);
        used += w;
      } else break;
    }

    setVisibleIds(vis);
  };

  useEffect(() => {
    recalc();
    window.addEventListener("resize", recalc);
    return () => window.removeEventListener("resize", recalc);
  }, [itemWidths]);

  useEffect(() => {
    if (selectedResourceType) {
      handleSelectResourceType(selectedResourceType);
    } else {
      handleSelectResourceType(defaultResourceType);
    }
  }, []);

  const visible = resourceTypes.filter((i) => visibleIds.includes(i.type));
  const overflow = resourceTypes.filter((i) => !visibleIds.includes(i.type));

  return (
    <div ref={containerRef} className="w-full">
      <HStack className="w-full" justifyContent={overflow.length > 0 ? "between" : "start"} gap={3}>
        {visible.map((type) => {
          const classActive = "border-primaryBlue font-semibold dark:border-brightBlue";
          const base =
            "px-1 text-sm cursor-pointer border-b-[2px] hover:font-semibold hover:border-primaryBlue dark:hover:border-brightBlue";
          return (
            <div
              key={type.type}
              className={`${base} ${resourceType === type.type ? classActive : "border-transparent"}`}
              onClick={() => handleSelectResourceType(type.type)}
            >
              {type.displayName || type.type}
            </div>
          );
        })}
        {overflow.length > 0 ? (
          <MenuResourceType
            resourceTypes={overflow}
            currentResourceType={resourceType}
            onAction={handleSelectResourceType}
          />
        ) : null}
      </HStack>
    </div>
  );
};

export default ResourceTypeToolbar;

type MenuResourceTypeProps = {
  resourceTypes: DefaultResourceTypeEntity[];
  currentResourceType: string;
  onAction: (type: string) => void;
};

export const MenuResourceType = ({ resourceTypes, currentResourceType, onAction }: MenuResourceTypeProps) => {
  const { isDarkMode } = useThemeStore();
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="relative inline-block" ref={wrapperRef}>
      <button
        className={`flex items-center justify-center rounded-sm overflow-hidden`}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((prev) => !prev);
        }}
      >
        <MoreHorizontal isDarkMode={isDarkMode} size={20} isActive={isOpen} />
        {/* <Icon
          src={isOpen ? MoreIconBlue : MoreIcon}
          srcDark={isOpen ? MoreIconBlueDark : MoreIconGray}
          srcHover={MoreIconBlue}
          srcDarkHover={MoreIconBlueDark}
          alt="more"
          className="w-5 h-5"
        /> */}
      </button>

      {isOpen ? (
        <div className="absolute text-sm  right-0 mt-1 min-w-28 bg-white dark:text-white dark:bg-black rounded-[4px] overflow-hidden shadow-elevationShadow4 z-10">
          <ul>
            {resourceTypes.map((item) => (
              <li
                className={`px-3 py-2 cursor-pointer hover:bg-extraLightGray dark:hover:bg-deepGray dark:text-white
              ${currentResourceType === item.type
                    ? "bg-softBlue hover:bg-softBlue dark:bg-midnightBlue dark:hover:bg-midnightBlue"
                    : ""
                  }`}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                  onAction(item.type);
                }}
              >
                {item.displayName || item.type}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};
